"use client";
import React, { useState, useEffect } from "react";
import { FaCalendarAlt, FaClock, FaMapMarkerAlt, FaExclamationCircle, FaTimes } from "react-icons/fa";
import { currencyFormatter } from "../../helpers/Helper";
import { useNavigate, useParams } from "react-router-dom";
import { PiMinusCircleDuotone, PiPlusCircleDuotone, PiShoppingCartDuotone } from "react-icons/pi";

import anime from "animejs";
import api from "../../helpers/Api";
import { Helmet } from "react-helmet-async";
import moment from "moment";
import MessageError from "../../components/MessageError";
import { SkeletonEventDetail } from "../../components/Skeleton";
import SeatPickerModal from "./components/SeatPickerModal";
import "moment/locale/es";
moment.locale("es");

export function Render({ detailevent }) {
    const [selectedQuantities, setSelectedQuantities] = useState({});
    const [loading, setloading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(false);
    const [seatModalOpen, setSeatModalOpen] = useState(false);
    const [activeSeatPackage, setActiveSeatPackage] = useState(null);
    const [showFullDesc, setShowFullDesc] = useState(false);

    const URL_ORIGIN = process.env.NODE_ENV === "development"
        ? process.env.REACT_APP_API_URL_DEV
        : process.env.REACT_APP_API_URL;

    const getPackageKey = (id) => String(id);

    const getSelectedCount = (packageId) => {
        const item = selectedQuantities?.[getPackageKey(packageId)];
        return Array.isArray(item?.tickets) ? item.tickets.length : (item?.quantity || 0);
    };

    const getSelectedTotal = () => {
        return Object.values(selectedQuantities || {}).reduce((sum, item) => {
            const qty = Array.isArray(item?.tickets) ? item.tickets.length : (item?.quantity || 0);
            const unitPrice = Number(item?.amount || 0);
            if (!qty) return sum;

            const pkg = (detailevent?.tickets || []).find(t => String(t.event_package_id) === String(item.id));
            const discount = pkg ? getActiveDiscount(pkg) : null;

            if (discount?.discount_type === "bogo") {
                const buyQty = Number(discount.buy_quantity || 0);
                const freeQty = Number(discount.free_quantity || 0);
                if (buyQty > 0 && freeQty > 0) {
                    const paidQty = qty - Math.floor(qty / buyQty) * freeQty;
                    return sum + paidQty * unitPrice;
                }
            }

            return sum + unitPrice * qty;
        }, 0);
    };

    const handleQuantityChange = (amount, newQuantity, id) => {
        const prevQuantity = getQty(id);
        const delta = newQuantity - prevQuantity;

        const currentTotal = Object.keys(selectedQuantities).reduce((sum, k) => sum + getQty(k), 0);
        const newTotal = currentTotal + delta;

        if (newTotal > detailevent.event_tickets_per_person) {
            setErrorMessage(`Solo puedes comprar ${detailevent.event_tickets_per_person} boletos en total`);
            return;
        }

        if (newQuantity < 0) return;

        setErrorMessage(null);

        setSelectedQuantities((prev) => {
            if (newQuantity === 0) {
                const copy = { ...prev };
                delete copy[id];
                return copy;
            }

            const tickets = Array.from({ length: newQuantity }, () => ({
                seatId: null,
                label: null
            }));

            return {
                ...prev,
                [id]: {
                    id,
                    amount,
                    quantity: newQuantity,
                    tickets,
                },
            };
        });
    };

    const getQty = (id) => {
        const item = selectedQuantities?.[id];
        if (!item) return 0;
        if (Array.isArray(item.tickets)) return item.tickets.length;
        return item.quantity || 0;
    };

    let navigate = useNavigate();

    const routeChange = async () => {
        setloading(true);
        setErrorMessage(null);

        try {
            const holdTickets = Object.entries(selectedQuantities)
                .filter(([_, data]) => (data?.tickets?.length || 0) > 0)
                .flatMap(([packageId, data]) => {
                    const pkgId = parseInt(packageId, 10);

                    return (data.tickets || []).map((t) => ({
                        event_package_id: pkgId,
                        seat_id: t.seatId ?? null,
                        label: t.label ?? null,
                    }));
                });

            if (holdTickets.length === 0) {
                setErrorMessage("Selecciona por lo menos un boleto");
                setloading(false);
                setTimeout(() => setErrorMessage(null), 2500);
                return;
            }

            const storedQueue = localStorage.getItem(`queue_token_${detailevent.event_id}`);
            const queueToken = storedQueue ? JSON.parse(storedQueue)?.token : null;

            const payload = {
                event_id: detailevent.event_id,
                customer_id: null,
                tickets: holdTickets,
                ...(queueToken && { queue_token: queueToken }),
            };

            const res = await api.post("/Event/Holds", payload);

            const holdId = res?.data?.hold_id;
            if (!holdId) {
                throw new Error("No se recibió hold_id del servidor");
            }

            setloading(false);
            navigate(`/payment/${holdId}`);
        } catch (error) {
            setloading(false);

            let backendMsg =
                error.response?.data?.message ||
                error.response?.data?.error ||
                "Ocurrió un error al procesar tu solicitud. Por favor, intenta de nuevo";

            if (error.response?.data?.error === "SEAT_CONFLICT" && Array.isArray(error.response?.data?.conflicts)) {
                const conflicts = error.response.data.conflicts;
                const conflictSeatIds = new Set(conflicts.map(c => String(c.seat_id)));

                let conflictPkgId = null;
                for (const [pkgId, data] of Object.entries(selectedQuantities)) {
                    if (Array.isArray(data.tickets) && data.tickets.some(t => conflictSeatIds.has(String(t.seatId)))) {
                        conflictPkgId = pkgId;
                        break;
                    }
                }

                setSelectedQuantities(prev => {
                    const updated = { ...prev };
                    for (const [pkgId, data] of Object.entries(updated)) {
                        if (!Array.isArray(data.tickets)) continue;
                        const filtered = data.tickets.filter(t => !conflictSeatIds.has(String(t.seatId)));
                        if (filtered.length === 0) {
                            delete updated[pkgId];
                        } else {
                            updated[pkgId] = { ...data, tickets: filtered, quantity: filtered.length };
                        }
                    }
                    return updated;
                });

                const affectedPkg = (detailevent.tickets || []).find(p => String(p.event_package_id) === String(conflictPkgId));
                if (affectedPkg) {
                    setActiveSeatPackage(affectedPkg);
                    setSeatModalOpen(true);
                }

                const seats = conflicts.map(c => c.label || c.seat_id).join(", ");
                setErrorMessage(`Los asientos ${seats} ya fueron tomados. Elige otros.`);
                setTimeout(() => setErrorMessage(null), 4000);
                return;
            }

            setErrorMessage(backendMsg);

            setTimeout(() => setErrorMessage(null), 5000);
        }
    };

    useEffect(() => {
        anime.timeline({ easing: "easeOutExpo", duration: 750 })
            .add({
                targets: ".detail-title, .detail-subtitle",
                translateY: [50, 0],
                opacity: [0, 1],
                delay: anime.stagger(200),
            })
            .add({
                targets: ".detail-info div",
                translateX: [-30, 0],
                opacity: [0, 1],
                delay: anime.stagger(200),
                offset: "-=500",
            });
    }, []);

    const nameParts = detailevent.event_name.trim().split(" ")
    const firstWord = nameParts[0]
    const restOfName = nameParts.slice(1).join(" ")
    const seatLayout = detailevent?.event_layout?.[0] || null;

    const seatLabelById = React.useMemo(() => {
        const map = {};
        (seatLayout?.blocks || []).forEach((b) => {
            (b.seats || []).forEach((s) => {
                map[s.id] = s.label || null;
            });
        });
        return map;
    }, [seatLayout]);

    const openSeatModal = (pkg) => {
        setActiveSeatPackage(pkg);
        setSeatModalOpen(true);
    };

    const closeSeatModal = () => {
        setSeatModalOpen(false);
        setActiveSeatPackage(null);
    };

    const getTotalSelected = () => {
        return Object.values(selectedQuantities || {}).reduce((sum, item) => {
            const qty = Array.isArray(item?.tickets) ? item.tickets.length : (item?.quantity || 0);
            return sum + qty;
        }, 0);
    };

    const activePkgId = activeSeatPackage?.event_package_id;
    const currentPkgCount = activePkgId ? getSelectedCount(activePkgId) : 0;
    const totalSelected = getTotalSelected();

    const remainingForActivePackage = Math.max(
        0,
        detailevent.event_tickets_per_person - (totalSelected - currentPkgCount)
    );

    const getFinalPrice = (pkg) => {
        const globalDiscounts = detailevent.event_discounts.filter(
            d => d.package_id == null && d.status
        );
        const allDiscounts = [...(pkg.discounts || []), ...globalDiscounts];
        let finalPrice = pkg.price;

        if (allDiscounts.length > 0) {
            const d = allDiscounts[0];
            if (d.discount_type === "bogo") return finalPrice;
            if (d.discount_type === "percentage") {
                finalPrice = finalPrice - (finalPrice * Number(d.discount_value) / 100);
            } else {
                finalPrice = finalPrice - Number(d.discount_value);
            }
        }

        return Math.max(0, finalPrice);
    };

    const getActiveDiscount = (pkg) => {
        const globalDiscounts = detailevent.event_discounts.filter(
            d => d.package_id == null && d.status
        );
        const allDiscounts = [...(pkg.discounts || []), ...globalDiscounts];
        return allDiscounts.length > 0 ? allDiscounts[0] : null;
    };

    const activeFinalPrice = activeSeatPackage ? getFinalPrice(activeSeatPackage) : 0;

    // SEO por evento (lo ve Google, que sí ejecuta JS; mejora títulos y descripción).
    const _seoImgId = (detailevent?.event_images || []).find((i) => i.is_primary)?.id
        ?? detailevent?.event_images?.[0]?.id;
    const seoImage = _seoImgId
        ? `${URL_ORIGIN}/image-proxy?id=${_seoImgId}`
        : "https://www.monoticket.mx/identity/LOGO_SHORT.png";
    const seoDesc = String(detailevent?.event_description || "Compra tus boletos en Monoticket.")
        .replace(/\s+/g, " ").trim().slice(0, 160);
    const seoTitle = `${detailevent?.event_name || "Evento"} | Monoticket`;

    return (
        <div className="w-full min-h-screen bg-[#0f0e17] text-white pb-28 lg:pb-0">
            <Helmet>
                <title>{seoTitle}</title>
                <meta name="description" content={seoDesc} />
                <meta property="og:title" content={seoTitle} />
                <meta property="og:description" content={seoDesc} />
                <meta property="og:type" content="website" />
                <meta property="og:image" content={seoImage} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={seoTitle} />
                <meta name="twitter:description" content={seoDesc} />
                <meta name="twitter:image" content={seoImage} />
            </Helmet>
            <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">

                <div className="relative w-full grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 py-6 lg:py-10 items-start">

                    <div className="flex flex-col gap-4">
                        <div className="relative w-full aspect-[3/4] overflow-hidden rounded-2xl border border-white/10 bg-black/20">
                            {detailevent.event_images.map((img) => {
                                if (img.is_primary) {
                                    return (
                                        <img
                                            key={img.id}
                                            src={`${URL_ORIGIN}/image-proxy?id=${img.id}`}
                                            alt={img.image_name || detailevent.event_name}
                                            fetchPriority="high"
                                            className="w-full h-full object-cover"
                                        />
                                    );
                                }
                                return null;
                            })}
                            <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black"></div>
                        </div>
                        <div className="rounded-2xl border border-white/10 overflow-hidden bg-black/20">
                            <div className="mt-6">
                                <h4 className="font-display text-lg sm:text-xl font-bold text-center text-[#f5f5f5] mb-3">
                                    Ubicación
                                </h4>

                                <div className="rounded-2xl border border-white/10 overflow-hidden bg-black/20">
                                    <iframe
                                        title="Ubicación"
                                        src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyDEbNjeAfXFx0xqgs3pH4IlCkF5aqtun3c&q=${detailevent.event_location}`}
                                        width="100%"
                                        height="400"
                                        className="w-full"
                                        allowFullScreen
                                        loading="lazy"
                                    ></iframe>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="w-full rounded-2xl border border-white/10 bg-[#16151c] p-4 sm:p-6 lg:p-6 flex flex-col gap-4">
                        <div className="text-center">
                            <h1 className="detail-title font-display text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight mb-3">
                                {firstWord}
                                {restOfName && (
                                    <>
                                        <br />
                                        <span className="text-transparent bg-clip-text gradient-monoparty-text">
                                            {restOfName}
                                        </span>
                                    </>
                                )}
                            </h1>

                            <div className="w-full bg-[#00000054] p-3 rounded-xl border border-white/10">
                                <p
                                    className={`detail-subtitle text-xs sm:text-sm text-gray-300 leading-relaxed whitespace-pre-line break-words ${showFullDesc ? "" : "max-h-[8.5rem] overflow-hidden"}`}
                                >
                                    {detailevent.event_description}
                                </p>

                                {String(detailevent.event_description || "").length > 220 && (
                                    <button
                                        type="button"
                                        onClick={() => setShowFullDesc(v => !v)}
                                        className="mt-2 text-xs sm:text-sm text-white/70 hover:text-white transition-colors underline underline-offset-4"
                                    >
                                        {showFullDesc ? "Ver menos" : "Ver más"}
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="detail-info flex flex-col sm:flex-row flex-wrap gap-2 text-gray-300 text-xs sm:text-sm justify-center">
                            <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-3 py-2">
                                <FaMapMarkerAlt className="text-[#f5f5f5]" />
                                <span className="truncate max-w-[300px] sm:max-w-[420px] lg:max-w-[520px]">
                                    {detailevent.event_location}
                                </span>
                            </div>

                            <div className="flex items-center gap-2 capitalize bg-white/5 border border-white/10 rounded-full px-3 py-2">
                                <FaCalendarAlt className="text-[#f5f5f5]" />
                                {moment(detailevent.event_date).utcOffset(0, true).format("dddd, D [de] MMMM [del] YYYY")}
                            </div>

                            <div className="flex items-center gap-2 capitalize bg-white/5 border border-white/10 rounded-full px-3 py-2">
                                <FaClock />
                                {moment(detailevent.event_date).utcOffset(0, true).format("hh:mm a")}
                            </div>
                        </div>

                        {errorMessage && (
                            <div className="fixed inset-0 flex items-center justify-center z-50 px-4">
                                <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setErrorMessage(null)}></div>
                                <div role="alertdialog" aria-modal="true" aria-live="assertive" aria-atomic="true" className="relative bg-gradient-to-br from-[#292834] to-[#1a1a24] rounded-2xl shadow-2xl max-w-md w-full p-6 transform animate-bounce-in border border-red-500/30">
                                    <button
                                        onClick={() => setErrorMessage(null)}
                                        className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
                                    >
                                        <FaTimes size={20} />
                                    </button>
                                    <div className="flex flex-col items-center text-center">
                                        <div className="bg-red-500/20 p-4 rounded-full mb-4">
                                            <FaExclamationCircle className="text-red-500 text-5xl" />
                                        </div>
                                        <h3 className="text-2xl font-bold text-white mb-2">¡Atención!</h3>
                                        <p className="text-gray-300 text-lg mb-6">{errorMessage}</p>
                                        <button
                                            onClick={() => setErrorMessage(null)}
                                            className="bg-red-500 hover:bg-red-600 text-white font-semibold py-3 px-8 rounded-lg transition-all transform hover:scale-105 shadow-lg"
                                        >
                                            Entendido
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {detailevent.event_sales_enabled ? (
                            <div className="w-full">
                                <div className="flex items-center gap-3 my-4">
                                    <span className="w-1 h-5 bg-brand rounded-full" />
                                    <h2 className="font-display text-base font-bold text-white">Selecciona tus boletos</h2>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {detailevent.tickets.map((price, index) => {
                                        const isSeated = price.has_seats === true;
                                        const appliedDiscount = getActiveDiscount(price);
                                        const finalPrice = getFinalPrice(price);
                                        const isLastOdd = index === detailevent.tickets.length - 1 && detailevent.tickets.length % 2 !== 0;

                                        return (
                                            <div key={price.event_package_id} className={`h-full rounded-2xl border border-white/10 bg-[#1e1d28] px-4 py-4 hover:border-brand/30 hover:bg-white/[0.03] transition ${isLastOdd ? "sm:col-span-2 sm:max-w-[50%] sm:mx-auto sm:w-full" : ""}`}>
                                                <div className="flex items-start justify-between gap-3">
                                                    <div className="min-w-0">
                                                        <span className="inline-flex items-center text-[11px] leading-none px-2 py-1 rounded-full bg-black/25 border border-white/10 text-white/70">
                                                            {isSeated ? "Asientos numerados" : "Entrada general"}
                                                        </span>

                                                        <h5 className="mt-2 text-sm sm:text-base font-semibold text-white truncate">
                                                            {price.category}
                                                        </h5>
                                                    </div>

                                                    <div className="shrink-0">
                                                        {price.sold_out == true ? (
                                                            <div className="px-3 py-2 rounded-full text-xs bg-[#595151] border border-white/10">
                                                                Agotados
                                                            </div>
                                                        ) : (
                                                            isSeated ? (
                                                                <button
                                                                    onClick={() => openSeatModal(price)}
                                                                    className="bg-brand text-white text-xs sm:text-sm font-semibold px-4 py-2.5 min-h-[44px] rounded-full hover:opacity-90 transition whitespace-nowrap"
                                                                >
                                                                    Seleccionar
                                                                </button>
                                                            ) : (
                                                                <div className="flex items-center">
                                                                    <button
                                                                        aria-label={`Quitar un boleto de ${price.category}`}
                                                                        className="bg-brand text-black rounded-full w-11 h-11 flex items-center justify-center"
                                                                        onClick={() =>
                                                                            handleQuantityChange(getFinalPrice(price), getQty(price.event_package_id) - 1, price.event_package_id)
                                                                        }
                                                                    >
                                                                        <PiMinusCircleDuotone size={20} className="text-white mr-[2px]" />
                                                                    </button>

                                                                    <span className="mx-3 sm:mx-4 text-white text-sm sm:text-base min-w-[20px] text-center">
                                                                        {getQty(price.event_package_id)}
                                                                    </span>

                                                                    <button
                                                                        aria-label={`Agregar un boleto de ${price.category}`}
                                                                        className="bg-brand text-black rounded-full w-11 h-11 flex items-center justify-center"
                                                                        onClick={() =>
                                                                            handleQuantityChange(getFinalPrice(price), getQty(price.event_package_id) + 1, price.event_package_id)
                                                                        }
                                                                    >
                                                                        <PiPlusCircleDuotone size={20} className="text-white mr-[1px]" />
                                                                    </button>
                                                                </div>
                                                            )
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="mt-3">
                                                    {appliedDiscount && appliedDiscount.discount_type === "bogo" ? (
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <span className="text-xl sm:text-2xl font-bold text-white font-mono">
                                                                {currencyFormatter(price.price)}
                                                            </span>
                                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-md bg-brand/20 border border-brand/40 text-brand text-xs font-bold uppercase tracking-wide font-mono">
                                                                {appliedDiscount.buy_quantity && appliedDiscount.free_quantity
                                                                    ? `${appliedDiscount.buy_quantity}x${appliedDiscount.buy_quantity - appliedDiscount.free_quantity}`
                                                                    : "Promo"}
                                                            </span>
                                                        </div>
                                                    ) : appliedDiscount ? (
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <span className="text-xs sm:text-sm text-gray-500 line-through font-mono">
                                                                {currencyFormatter(price.price)}
                                                            </span>
                                                            <span className="text-xl sm:text-2xl font-bold text-white font-mono">
                                                                {currencyFormatter(finalPrice)}
                                                            </span>
                                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-md bg-green-500/15 border border-green-500/30 text-green-400 text-xs font-semibold">
                                                                {appliedDiscount.discount_type === "percentage"
                                                                    ? `−${parseInt(appliedDiscount.discount_value)}%`
                                                                    : `−$${parseInt(appliedDiscount.discount_value)}`}
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <span className="text-xl sm:text-2xl font-bold text-white font-mono">
                                                            {currencyFormatter(price.price)}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                <div className="mt-4 rounded-xl border border-white/10 bg-[#0d0c14] p-4">
                                    <div className="text-white font-semibold mb-3 flex items-center gap-2">
                                        <span className="w-1 h-4 bg-brand rounded-full" />
                                        Resumen
                                    </div>

                                    {getSelectedTotal() === 0 ? (
                                        <p className="text-gray-500 text-sm text-center py-2">
                                            Selecciona boletos para ver el resumen
                                        </p>
                                    ) : (
                                        (detailevent?.tickets || []).map((t) => {
                                            const qty = getSelectedCount(t.event_package_id);
                                            if (!qty) return null;

                                            const subtotal = qty * getFinalPrice(t);
                                            const item = selectedQuantities?.[String(t.event_package_id)];
                                            const hasSeats = t.has_seats;

                                            return (
                                                <div key={t.event_package_id} className="mb-3">
                                                    <div className="flex justify-between text-white/80 text-sm">
                                                        <div>
                                                            {t.category} <span className="text-white/50">× {qty}</span>
                                                        </div>
                                                        <div className="font-mono">{currencyFormatter(subtotal)}</div>
                                                    </div>

                                                    {hasSeats && Array.isArray(item?.tickets) && item.tickets.length > 0 && (
                                                        <div className="mt-1 text-xs text-white/50 flex flex-wrap gap-2">
                                                            {item.tickets
                                                                .filter((x) => x.seatId)
                                                                .slice(0, 10)
                                                                .map((x) => (
                                                                    <span key={x.seatId} className="px-2 py-1 rounded bg-white/5 border border-white/10">
                                                                        {x.label != null ? `TK-${x.label}` : x.seatId}
                                                                    </span>
                                                                ))}
                                                            {item.tickets.filter((x) => x.seatId).length > 10 && (
                                                                <span className="px-2 py-1 rounded bg-white/5 border border-white/10">
                                                                    +{item.tickets.filter((x) => x.seatId).length - 10} más
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })
                                    )}

                                    {getSelectedTotal() > 0 && (() => {
                                        const lines = (detailevent?.tickets || []).flatMap(t => {
                                            const qty = getSelectedCount(t.event_package_id);
                                            if (!qty) return [];
                                            const discount = getActiveDiscount(t);
                                            if (!discount) return [];

                                            const unitPrice = t.price;
                                            const fullAmt = qty * unitPrice;
                                            let saved = 0;

                                            if (discount.discount_type === "bogo") {
                                                const bq = Number(discount.buy_quantity || 0);
                                                const fq = Number(discount.free_quantity || 0);
                                                if (bq > 0 && fq > 0) saved = Math.floor(qty / bq) * fq * unitPrice;
                                            } else if (discount.discount_type === "percentage") {
                                                saved = fullAmt * (Number(discount.discount_value || 0) / 100);
                                            } else {
                                                saved = Math.min(Number(discount.discount_value || 0), fullAmt);
                                            }

                                            if (saved <= 0) return [];

                                            let label;
                                            if (discount.discount_type === "bogo") {
                                                label = `Promoción ${discount.buy_quantity}x${discount.buy_quantity - discount.free_quantity}`;
                                            } else if (discount.discount_type === "percentage") {
                                                label = `Descuento ${parseInt(discount.discount_value)}%`;
                                            } else {
                                                label = `Descuento`;
                                            }
                                            return [{ label, saved }];
                                        });

                                        if (!lines.length) return null;
                                        return (
                                            <div className="mt-2 mb-1 space-y-1.5">
                                                {lines.map((dl, i) => (
                                                    <div key={i} className="flex justify-between items-center text-sm">
                                                        <span className="flex items-center gap-1.5 text-green-400">
                                                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-green-500/15 border border-green-500/25 uppercase tracking-wide">
                                                                {dl.label}
                                                            </span>
                                                        </span>
                                                        <span className="text-green-400 font-semibold font-mono">−{currencyFormatter(dl.saved)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        );
                                    })()}

                                    <div className="border-t border-white/10 my-3" />

                                    <div className="flex justify-between font-semibold">
                                        <span className="text-gray-300">SubTotal</span>
                                        <span className={`font-mono ${getSelectedTotal() > 0 ? "text-brand" : "text-gray-500"}`}>
                                            {currencyFormatter(getSelectedTotal())}
                                        </span>
                                    </div>
                                </div>

                                <div className="mt-4 flex justify-center">
                                    <button
                                        className={`buy-btn event-one__btn thm-btn w-full ${(loading || getSelectedTotal() === 0) ? "thm-btn__disabled" : ""}`}
                                        onClick={routeChange}
                                        disabled={loading || getSelectedTotal() === 0}
                                    >
                                        <span className="flex items-center justify-center">
                                            <PiShoppingCartDuotone size={22} className="mr-2" />
                                            {loading ? "Reservando tus boletos..." : "Comprar"}
                                        </span>
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="w-full">
                                    <h4 className="font-display text-xl md:text-2xl font-bold text-center text-[#f5f5f5] my-2">
                                        Próximamente más información de boletos
                                    </h4>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {detailevent.event_sales_enabled && getSelectedTotal() > 0 && (
                    <div className="fixed bottom-0 left-0 right-0 z-[45] lg:hidden px-4 pb-16 pt-6 bg-gradient-to-t from-[#0f0e17] via-[#0f0e17]/95 to-transparent">
                        <div className="flex items-center justify-between gap-3 bg-[#16151c] border border-white/10 rounded-2xl px-4 py-3 shadow-2xl">
                            <div className="text-sm text-gray-300 leading-tight">
                                <div className="font-semibold text-white font-mono">{getSelectedTotal().toLocaleString("es-MX", { style: "currency", currency: "MXN" })}</div>
                                <div className="text-xs text-gray-400">{Object.values(selectedQuantities).reduce((s, i) => s + (Array.isArray(i?.tickets) ? i.tickets.length : (i?.quantity || 0)), 0)} boleto(s) seleccionado(s)</div>
                            </div>
                            <button
                                className={`event-one__btn thm-btn px-6 py-2.5 text-sm ${loading ? "thm-btn__disabled" : ""}`}
                                onClick={routeChange}
                                disabled={loading}
                            >
                                <PiShoppingCartDuotone size={18} className="mr-1.5" />
                                {loading ? "Reservando tus boletos..." : "Comprar"}
                            </button>
                        </div>
                    </div>
                )}

                <SeatPickerModal
                    open={seatModalOpen}
                    eventId={detailevent.event_id}
                    onClose={closeSeatModal}
                    activePackage={activeSeatPackage}
                    finalPrice={activeFinalPrice}
                    layout={seatLayout}
                    maxPerPerson={remainingForActivePackage}
                    initialSelection={{
                        seatIds: (selectedQuantities?.[String(activeSeatPackage?.event_package_id)]?.tickets || [])
                            .map(t => t.seatId)
                            .filter(Boolean),
                        qty: (selectedQuantities?.[String(activeSeatPackage?.event_package_id)]?.tickets || []).length,
                    }}
                    onConfirm={(payload) => {
                        const packageId = String(payload.packageId);
                        const pkg = activeSeatPackage;
                        const amount = getFinalPrice(pkg);

                        setSelectedQuantities((prev) => {
                            const seatIds = Array.isArray(payload.seatIds) ? payload.seatIds : [];
                            const qty = Number(payload.qty || 0);

                            const tickets = pkg?.has_seats
                                ? seatIds.map((seatId) => ({
                                    seatId,
                                    label: seatLabelById[seatId] || null,
                                }))
                                : Array.from({ length: qty }, () => ({
                                    seatId: null,
                                    label: null,
                                }));

                            if (tickets.length === 0) {
                                const copy = { ...prev };
                                delete copy[packageId];
                                return copy;
                            }

                            return {
                                ...prev,
                                [packageId]: {
                                    id: packageId,
                                    amount,
                                    quantity: tickets.length,
                                    tickets,
                                },
                            };
                        });

                        setSeatModalOpen(false);
                    }}
                />
            </div>
        </div>
    );
}

export default function Detail() {
    const { id } = useParams()
    const [detailevent, setdetailEvent] = useState(null);
    const [error, setError] = useState();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const detaileventsResponse = await api.get(`/Event/ById?id=${id}`)
                if (detaileventsResponse.data.found && detaileventsResponse.data.event_enabled) {
                    setdetailEvent(detaileventsResponse.data);
                } else if (detaileventsResponse.data.found && !detaileventsResponse.data.event_enabled) {
                    setError("Lo sentimos, este evento ya no está disponible");
                } else {
                    setError("Lo sentimos, el evento que buscas no existe o ya no está disponible");
                }
            } catch (error) {
                console.error("Error fetching detailevent data:", error);
                setError("Ocurrió un error al obtener el evento, por favor intenta de nuevo");
            }
        };

        fetchData();
    }, []);

    if (error) {
        return (
            <MessageError
                title="No se pudo cargar el evento"
                description={error}
            />
        );
    }

    if (!detailevent) return <SkeletonEventDetail />;

    return <Render detailevent={detailevent} />;
} 